import React, { useState, useEffect } from 'react';
import { Row, Col, Label } from 'reactstrap';
import DatePicker from 'react-datepicker';
import { Button, Icon } from '../Component';
import { format, startOfDay, endOfDay, subMinutes } from 'date-fns';



/**
 * Date Range Time Filter Component
 * Provides from/to date selection with time options
 *
 * @param {Object} props
 * @param {Date} props.filterDate - Current filter date (from date)
 * @param {Date} props.filterEndDate - Current filter end date (to date)
 * @param {Function} props.onFilterChange - Callback when filter changes
 * @param {number} props.messageCount - Number of messages for display
 * @param {Object} props.activity - Activity object containing date information
 */
const TimeFilter = ({ filterDate, filterEndDate, onFilterChange, messageCount, activity }) => {
    const [fromDate, setFromDate] = useState(filterDate || startOfDay(new Date()));
    const [toDate, setToDate] = useState(filterEndDate || endOfDay(new Date()));

    useEffect(() => {
        // Dynamically import the CSS for react-datepicker
        import('react-datepicker/dist/react-datepicker.css');
    }, []); // The empty dependency array ensures this runs only once when the component mounts

    // Update local state when props change
    useEffect(() => {
        if (filterDate) setFromDate(filterDate);
        if (filterEndDate) setToDate(filterEndDate);
    }, [filterDate, filterEndDate]);

    // Handle from date change and ensure to date is not before from date
    const handleFromDateChange = (date) => {
        setFromDate(date);
        if (toDate && date && date > toDate) {
            setToDate(endOfDay(date));
        }
    };

    // Handle to date change and ensure it's not before from date
    const handleToDateChange = (date) => {
        setToDate(date);
        if (fromDate && date && date < fromDate) {
            setFromDate(startOfDay(date));
        }
    };

    const handleApplyFilter = () => {
        if (fromDate && toDate) {
            onFilterChange(fromDate, toDate);
        }
    };

    const handleReset = () => {
        if (activity?.date) {
            // Use activity date minus 5 minutes as the default from date
            const activityDate = new Date(activity.date);
            const defaultFromDate = subMinutes(activityDate, 15);
            const defaultToDate = endOfDay(activityDate);

            setFromDate(defaultFromDate);
            setToDate(defaultToDate);
            onFilterChange(defaultFromDate, defaultToDate);
        } else {
            // Fallback to today if no activity date
            const today = new Date();
            const startToday = startOfDay(today);
            const endToday = endOfDay(today);

            setFromDate(startToday);
            setToDate(endToday);
            onFilterChange(startToday, endToday);
        }
    };

    return (
        <div className="time-filter" role="region" aria-label="Message time filter">
            {/* Date Range Filter */}
            <div className="date-range-filter">
                <div className="d-flex align-items-center mb-2">
                    <small className="text-muted fw-semibold">
                        <Icon name="calendar" className="me-1" />
                        Date Range Filter:
                    </small>
                </div>

                <Row className="align-items-end g-2">
                    <Col md={4} sm={6}>
                        <Label className="form-label small fw-semibold mb-1">From Date & Time</Label>
                        <DatePicker
                            selected={fromDate}
                            onChange={handleFromDateChange}
                            className="form-control form-control-sm"
                            placeholderText="Select from date"
                            dateFormat="dd/MM/yyyy HH:mm"
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            maxDate={toDate || new Date()}
                            popperClassName="datepicker-zindex"
                            calendarClassName="shadow-sm"
                            wrapperClassName="d-block"
                            autoComplete="off"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                        />
                    </Col>
                    <Col md={4} sm={6}>
                        <Label className="form-label small fw-semibold mb-1">To Date & Time</Label>
                        <DatePicker
                            selected={toDate}
                            onChange={handleToDateChange}
                            className="form-control form-control-sm"
                            placeholderText="Select to date"
                            dateFormat="dd/MM/yyyy HH:mm"
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            minDate={fromDate}
                            maxDate={new Date()}
                            popperClassName="datepicker-zindex"
                            calendarClassName="shadow-sm"
                            wrapperClassName="d-block"
                            autoComplete="off"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                        />
                    </Col>
                    <Col md={4} sm={12}>

                        <Button
                            color="primary"
                            onClick={handleApplyFilter}
                            disabled={!fromDate || !toDate}
                            title="Apply filter"
                            className="px-3 mx-2"
                        >
                            <Icon name="check" className="me-1" />
                            Apply
                        </Button>
                        <Button
                            color="secondary"
                            onClick={handleReset}
                            title="Reset to today"
                            className="px-3"
                        >
                            <Icon name="refresh" className="me-1" />
                            Reset
                        </Button>

                    </Col>
                </Row>

                {/* Current Filter Display */}
                {fromDate && toDate && (
                    <div className="mt-2">
                        <small className="text-muted">
                            <Icon name="filter" className="me-1" />
                            Showing messages from: <strong>{format(fromDate, 'dd/MM/yyyy HH:mm')}</strong>
                            {' '} to <strong>{format(toDate, 'dd/MM/yyyy HH:mm')}</strong>
                        </small>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TimeFilter;